import type { SxProps, Theme } from '@mui/material'

/**
 * Shared MUI ``sx`` for table header cells across the admin panel.
 * Uppercase + letter-spaced + smaller + secondary colour - the classic
 * table-header treatment - so headers don't visually compete with bold
 * row labels in the body.
 *
 * Used by:
 *  - ``components/admin/FpResourcesTable.tsx``
 *  - ``pages/admin/FranchisePartnerAccessTab.tsx``
 *
 * Drop into any new admin table's ``<TableCell sx={ADMIN_TABLE_HEADER_SX}>``.
 */
export const ADMIN_TABLE_HEADER_SX: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: '0.7rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  backgroundColor: '#FAFAFA',
  borderBottom: '2px solid #E0E0E0',
}

/** Resting colour for inline-table action icon buttons (slate; no theme token). */
export const INLINE_ACTION_NEUTRAL = '#6B7280'

/**
 * Shared MUI ``sx`` factory for the small action icon buttons inside inline
 * edit tables (open / edit / delete / lookup). A 32x32 neutral button that
 * tints to ``hoverColor`` on hover and greys out when disabled.
 *
 * Used by:
 *  - ``components/shared/referenceLinks/ReferenceLinksEditor.tsx``
 *  - ``components/admin/FpResourcesTable.tsx``
 *  - ``pages/admin/FranchisePartnerAccessTab.tsx``
 *  - ``pages/admin/UserForm.tsx``
 */
export const inlineActionIconSx = (hoverColor: string): SxProps<Theme> => ({
  width: 32,
  height: 32,
  color: INLINE_ACTION_NEUTRAL,
  borderRadius: 1,
  '&:hover': {
    color: hoverColor,
    backgroundColor: `${hoverColor}14`,
  },
  '&.Mui-disabled': {
    color: '#CBD5E1',
  },
})

/**
 * Compact font + control height for ``Select`` / ``TextField`` inputs rendered
 * inside an inline edit-table row. Smaller font keeps more URL/text visible
 * without truncation.
 *
 * Used by:
 *  - ``components/shared/referenceLinks/ReferenceLinksEditor.tsx``
 */
export const INLINE_EDIT_INPUT_SX: SxProps<Theme> = {
  '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 },
  '& .MuiSelect-select': { fontSize: '0.8rem', py: 0.75 },
  '& .MuiInputLabel-root': { fontSize: '0.78rem' },
  '& .MuiFormHelperText-root': { fontSize: '0.7rem', mx: 0.5 },
}
